let timers: number[] = [];
let chosenVoice: SpeechSynthesisVoice | null = null;
let voicesHooked = false;
let speakGen = 0;

function clearTimers() {
  for (const id of timers) window.clearTimeout(id);
  timers = [];
}

function hookVoices() {
  if (voicesHooked || !("speechSynthesis" in window)) return;
  voicesHooked = true;
  const refresh = () => {
    chosenVoice = null;
    pickVoice();
  };
  window.speechSynthesis.addEventListener("voiceschanged", refresh);
  pickVoice();
}

function voiceScore(voice: SpeechSynthesisVoice) {
  const name = voice.name.toLowerCase();
  const lang = (voice.lang || "").toLowerCase();
  if (!lang.startsWith("en")) return -50;

  let score = 0;
  if (lang.startsWith("en-us")) score += 10;
  else if (lang.startsWith("en-gb") || lang.startsWith("en-au") || lang.startsWith("en-ie")) score += 6;
  else score += 2;
  if (voice.localService) score += 5;

  const prefer = [
    "samantha",
    "nicky",
    "ava",
    "allison",
    "zoe",
    "susan",
    "karen",
    "moira",
    "daniel",
    "aaron",
    "tom",
    "google us english",
    "google uk english",
    "microsoft aria",
    "microsoft jenny",
    "microsoft guy",
    "microsoft ana",
    "natural",
    "enhanced",
    "premium",
    "neural",
    "siri",
  ];
  for (const needle of prefer) {
    if (name.includes(needle)) score += 14;
  }

  const avoid = [
    "espeak",
    "compact",
    "fred",
    "bells",
    "trinoids",
    "boing",
    "zarvox",
    "whisper",
    "deranged",
    "hysterical",
    "bad news",
    "good news",
    "junior",
    "princess",
    "cellos",
    "organ",
    "albert",
    "bubbles",
    "bahh",
    "pipes",
  ];
  for (const needle of avoid) {
    if (name.includes(needle)) score -= 45;
  }
  return score;
}

function pickVoice(): SpeechSynthesisVoice | null {
  if (!("speechSynthesis" in window)) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return chosenVoice;
  if (chosenVoice && voices.includes(chosenVoice)) return chosenVoice;
  const ranked = voices.slice().sort((a, b) => voiceScore(b) - voiceScore(a));
  chosenVoice = ranked[0] && voiceScore(ranked[0]) > 0 ? ranked[0] : ranked[0] ?? null;
  return chosenVoice;
}

const LETTER_SAY: Record<string, string> = {
  B: "Bee",
  I: "Eye",
  N: "En",
  G: "Gee",
  O: "Oh",
};

const ONES = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];
const TEENS = [
  "ten",
  "eleven",
  "twelve",
  "thirteen",
  "fourteen",
  "fifteen",
  "sixteen",
  "seventeen",
  "eighteen",
  "nineteen",
];
const TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy"];

function numberWords(n: number) {
  if (n < 10) return ONES[n] ?? String(n);
  if (n < 20) return TEENS[n - 10] ?? String(n);
  const tens = Math.floor(n / 10);
  const ones = n % 10;
  const tenWord = TENS[tens] ?? String(tens * 10);
  return ones === 0 ? tenWord : `${tenWord} ${ONES[ones]}`;
}

function spokenPhrase(text: string, pause = false) {
  const trimmed = text.trim();
  if (/^bingo!?$/i.test(trimmed)) return "Bingo!";
  const match = trimmed.match(/^([BINGO])\s*[-,]?\s*(\d{1,2})$/i);
  if (!match) return trimmed;
  const letter = LETTER_SAY[match[1].toUpperCase()] ?? match[1];
  const num = Number(match[2]);
  if (!Number.isInteger(num) || num < 1 || num > 75) return trimmed;
  const words = numberWords(num);
  return pause ? `${letter}, ${words}.` : `${letter} ${words}`;
}

function speakUtterance(text: string, pitch: number, rate: number, onDone: () => void) {
  const utterance = new SpeechSynthesisUtterance(text);
  const voice = pickVoice();
  if (voice) {
    utterance.voice = voice;
    utterance.lang = voice.lang || "en-US";
  } else {
    utterance.lang = "en-US";
  }
  utterance.rate = rate;
  utterance.pitch = pitch;
  utterance.volume = 1;
  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    onDone();
  };
  utterance.onend = finish;
  utterance.onerror = finish;
  const safety = window.setTimeout(finish, Math.max(1800, text.length * 140));
  timers.push(safety);
  try {
    window.speechSynthesis.speak(utterance);
  } catch {
    finish();
  }
}

export function unlockSpeech() {
  if (!("speechSynthesis" in window)) return;
  hookVoices();
  try {
    const utterance = new SpeechSynthesisUtterance(" ");
    utterance.volume = 0;
    const voice = pickVoice();
    if (voice) utterance.voice = voice;
    window.speechSynthesis.speak(utterance);
    window.speechSynthesis.cancel();
  } catch {
    /* private mode / unsupported */
  }
}

export function speakText(text: string, repeats = 1, pitch = 1, pause = false) {
  if (!("speechSynthesis" in window)) return;
  window.speechSynthesis.cancel();
  clearTimers();
  hookVoices();
  const gen = ++speakGen;

  const phrase = spokenPhrase(text, pause);
  const count = Math.max(1, Math.min(5, repeats));
  const isBingo = /^bingo/i.test(phrase);
  const rate = isBingo ? 1 : 0.94;
  const spokenPitch = isBingo ? Math.max(pitch, 1) : pitch;

  const run = (index: number) => {
    if (gen !== speakGen) return;
    if (index >= count) return;
    const gap = index === 0 ? 60 : 320;
    const id = window.setTimeout(() => {
      if (gen !== speakGen) return;
      speakUtterance(phrase, spokenPitch, rate, () => run(index + 1));
    }, gap);
    timers.push(id);
  };

  const start = () => {
    if (gen !== speakGen) return;
    run(0);
  };

  if (pickVoice() || window.speechSynthesis.getVoices().length) {
    start();
    return;
  }

  const onVoices = () => {
    window.speechSynthesis.removeEventListener("voiceschanged", onVoices);
    start();
  };
  window.speechSynthesis.addEventListener("voiceschanged", onVoices);
  const wait = window.setTimeout(() => {
    window.speechSynthesis.removeEventListener("voiceschanged", onVoices);
    start();
  }, 400);
  timers.push(wait);
}

export function cancelSpeech() {
  speakGen += 1;
  clearTimers();
  if ("speechSynthesis" in window) window.speechSynthesis.cancel();
}
